import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { AppConfig } from '../../app-config';
import { APP_CONFIG } from '../../app-config.token';

/**
 * Configuration for registering a hub event listener.
 */
export interface HubEventRegistration<T = unknown> {
  /** Server-side event name (e.g. 'AgreementsInvalidated') */
  eventName: string;
  /** Subject to push received events into */
  subject: Subject<T>;
}

/**
 * Generic base class for SignalR hub services.
 * Handles connection lifecycle, institution group membership, auto-reconnect and event wiring.
 *
 * Subclasses only need to provide the hub path and register event listeners.
 *
 * Uses skipNegotiation + WebSockets-only to bypass OpenTelemetry's
 * instrumentation-fetch which breaks the negotiate POST request.
 *
 * Usage:
 * ```
 * @Injectable({ providedIn: 'root' })
 * export class MyHubService extends RealtimeHubService {
 *   private readonly mySubject = new Subject<MyEvent>();
 *   readonly myEvent$ = this.mySubject.asObservable();
 *
 *   protected readonly hubPath = 'myhub';
 *   protected readonly eventRegistrations = [
 *     { eventName: 'MyEvent', subject: this.mySubject },
 *   ];
 * }
 * ```
 */
@Injectable()
export abstract class RealtimeHubService implements OnDestroy {
  private config = inject<AppConfig>(APP_CONFIG);
  private hubConnection: signalR.HubConnection | null = null;
  private joinedGroupIds = new Set<number>();

  private readonly reconnectedSubject = new Subject<void>();
  private readonly connectedSubject = new BehaviorSubject<boolean>(false);

  /** Emits when the connection is restored after a drop */
  readonly reconnected$: Observable<void> = this.reconnectedSubject.asObservable();

  /** Current connection state */
  readonly connected$: Observable<boolean> = this.connectedSubject.asObservable();

  /** Relative hub path appended to resourceApiURI (e.g. 'workeragreementshub') */
  protected abstract readonly hubPath: string;

  /** Event registrations — each maps a server event name to a Subject */
  protected abstract readonly eventRegistrations: HubEventRegistration[];

  async connect(): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    // Don't recreate while in a transient state — let withAutomaticReconnect handle it
    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      return;
    }

    this.hubConnection = null;

    const hubUrl = `${this.config.resourceApiURI}/${this.hubPath}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 1000, 2000, 5000, 10_000, 30_000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Wire up event listeners from subclass registrations
    for (const reg of this.eventRegistrations) {
      this.hubConnection.on(reg.eventName, (event: unknown) => reg.subject.next(event));
    }

    this.hubConnection.onreconnecting(() => this.connectedSubject.next(false));

    this.hubConnection.onreconnected(async () => {
      this.connectedSubject.next(true);
      for (const id of this.joinedGroupIds) {
        await this.invokeIfConnected('JoinInstitutionGroup', id);
      }
      this.reconnectedSubject.next();
    });

    this.hubConnection.onclose(() => this.connectedSubject.next(false));

    await this.hubConnection.start();
    this.connectedSubject.next(true);
  }

  async disconnect(): Promise<void> {
    this.joinedGroupIds.clear();
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
      this.connectedSubject.next(false);
    }
  }

  async joinInstitutionGroup(institutionId: number): Promise<void> {
    this.joinedGroupIds.add(institutionId);
    await this.invokeIfConnected('JoinInstitutionGroup', institutionId);
  }

  async leaveInstitutionGroup(institutionId: number): Promise<void> {
    this.joinedGroupIds.delete(institutionId);
    await this.invokeIfConnected('LeaveInstitutionGroup', institutionId);
  }

  /** Invoke a hub method if connected. Safe to call in any state. */
  protected async invokeIfConnected(method: string, ...args: unknown[]): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      await this.hubConnection.invoke(method, ...args);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.reconnectedSubject.complete();
    this.connectedSubject.complete();
    for (const reg of this.eventRegistrations) {
      reg.subject.complete();
    }
  }
}
